import { useVirtualizer } from '@tanstack/react-virtual';
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable, type SortingState } from '@tanstack/react-table';
import { useMemo, useRef, useState } from 'react';
import type { ContractSpec, InstrumentMarket, OrderView, PositionView } from '@head-of-trading/contracts';
import { useLanguage } from '../../i18n/context.js';
import { getTerminalCopy } from '../../i18n/terminal.js';
import { formatInteger, formatPct, formatPrice } from '../../lib/format.js';

interface WatchRow {
  readonly symbol: string;
  readonly contract: ContractSpec;
  readonly market: InstrumentMarket;
  readonly position: number;
  readonly activeOrders: number;
}

interface WatchlistProps {
  readonly instruments: readonly ContractSpec[];
  readonly markets: Readonly<Record<string, InstrumentMarket>>;
  readonly positions: readonly PositionView[];
  readonly orders: readonly OrderView[];
  readonly defaultSymbols: readonly string[];
  readonly sessionDate: string;
  readonly selectedSymbol: string;
  readonly simulatedAt: number;
  readonly onSelect: (symbol: string) => void;
}

const helper = createColumnHelper<WatchRow>();

export function Watchlist({ instruments, markets, positions, orders, defaultSymbols, sessionDate, selectedSymbol, simulatedAt, onSelect }: WatchlistProps) {
  const { language, locale } = useLanguage();
  const copy = getTerminalCopy(language).watchlist;
  const [sorting, setSorting] = useState<SortingState>([{ id: 'symbol', desc: false }]);
  const [query, setQuery] = useState('');
  const [watching, setWatching] = useState<ReadonlySet<string>>(() => new Set(defaultSymbols.filter((symbol) => markets[symbol])));
  const viewportRef = useRef<HTMLDivElement>(null);
  const positionMap = useMemo(() => new Map(positions.map((position) => [position.symbol, position.quantity])), [positions]);
  const activeMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const order of orders) if (!['FILLED', 'CANCELED', 'EXPIRED', 'REJECTED', 'CANCELED_END'].includes(order.status)) map.set(order.symbol, (map.get(order.symbol) ?? 0) + 1);
    return map;
  }, [orders]);
  const universe = useMemo(() => instruments.flatMap((contract) => {
    const market = markets[contract.symbol];
    return market ? [{ symbol: contract.symbol, contract, market, position: positionMap.get(contract.symbol) ?? 0, activeOrders: activeMap.get(contract.symbol) ?? 0 }] : [];
  }), [instruments, markets, positionMap, activeMap]);
  const normalizedQuery = query.trim().toLocaleLowerCase(locale);
  const rows = useMemo(() => universe.filter((row) => watching.has(row.symbol) && (!normalizedQuery || `${row.symbol} ${row.contract.root} ${row.contract.description} ${row.contract.exchange}`.toLocaleLowerCase(locale).includes(normalizedQuery))), [locale, universe, watching, normalizedQuery]);
  const searchResults = normalizedQuery ? universe.filter((row) => !watching.has(row.symbol) && `${row.symbol} ${row.contract.root} ${row.contract.description}`.toLocaleLowerCase(locale).includes(normalizedQuery)).slice(0, 5) : [];

  const columns = useMemo(() => [
    helper.accessor('symbol', { header: copy.instrument, cell: (value) => <div className="instrument-cell"><strong>{value.getValue()}</strong><small>@{value.row.original.contract.exchange}</small></div> }),
    helper.accessor((row) => row.contract.description, { id: 'description', header: copy.description, cell: (value) => <span title={value.getValue()}>{value.getValue()}</span> }),
    helper.accessor('position', { header: copy.position, cell: (value) => <span className={value.getValue() > 0 ? 'positive' : value.getValue() < 0 ? 'negative' : ''}>{formatInteger(value.getValue(), language)}</span> }),
    helper.accessor((row) => row.market.bid, { id: 'bid', header: copy.bid, cell: (value) => <span className="bid-text">{formatPrice(value.getValue(), value.row.original.contract, language)}</span> }),
    helper.accessor((row) => row.market.ask, { id: 'ask', header: copy.ask, cell: (value) => <span className="ask-text">{formatPrice(value.getValue(), value.row.original.contract, language)}</span> }),
    helper.accessor((row) => row.market.mid, { id: 'mid', header: copy.mid, cell: (value) => formatPrice(value.getValue(), value.row.original.contract, language) }),
    helper.accessor((row) => row.market.changePct, { id: 'change', header: copy.change, cell: (value) => <span className={value.getValue() >= 0 ? 'positive' : 'negative'}>{formatPct(value.getValue(), 2, language)}</span> }),
    helper.accessor('activeOrders', { header: copy.orders, cell: (value) => value.getValue() || '—' }),
    helper.accessor((row) => row.contract.lastTradeDate ? Math.max(0, Math.ceil((new Date(`${row.contract.lastTradeDate}T00:00:00Z`).getTime() - new Date(`${sessionDate}T00:00:00Z`).getTime()) / 86_400_000)) : null, { id: 'dte', header: copy.days, cell: (value) => value.getValue() ?? '—' }),
    helper.accessor((row) => row.contract.expiry, { id: 'expiry', header: copy.expiry, cell: (value) => value.getValue() ? new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value.getValue()}-01T00:00:00Z`)) : '—' }),
    helper.accessor((row) => row.contract.betaSp500, { id: 'beta', header: copy.beta, cell: (value) => value.getValue() === null ? '—' : new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value.getValue() ?? 0) })
  ], [copy, language, locale, sessionDate]);

  const table = useReactTable({ data: rows, columns, state: { sorting }, onSortingChange: setSorting, getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel() });
  const tableRows = table.getRowModel().rows;
  const virtualizer = useVirtualizer({ count: tableRows.length, getScrollElement: () => viewportRef.current, estimateSize: () => 34, overscan: 8 });
  const virtualRows = virtualizer.getVirtualItems();

  const remove = (symbol: string) => setWatching((current) => { const next = new Set(current); next.delete(symbol); return next; });
  const add = (symbol: string) => setWatching((current) => new Set([...current, symbol]));

  return (
    <section className="watchlist terminal-panel" aria-label={copy.aria}>
      <header className="terminal-panel__head"><div><span>{copy.eyebrow}</span><h2>{copy.title}</h2></div><span className="terminal-count">{rows.length}/{instruments.length}</span></header>
      <div className="watch-search"><span aria-hidden="true">⌕</span><input aria-label={copy.searchLabel} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.searchPlaceholder} /></div>
      {searchResults.length > 0 && <div className="watch-search-results">{searchResults.map((row) => <button key={row.symbol} onClick={() => add(row.symbol)}><strong>＋ {row.symbol}</strong><span>{row.contract.description}</span></button>)}</div>}
      <div className="watch-table" ref={viewportRef}>
        <table style={{ width: table.getCenterTotalSize() }}>
          <thead>{table.getHeaderGroups().map((group) => <tr key={group.id}>{group.headers.map((header) => <th className={`col-${header.column.id}`} style={{ width: header.getSize() }} key={header.id}><button onClick={header.column.getToggleSortingHandler()}>{flexRender(header.column.columnDef.header, header.getContext())}<span>{header.column.getIsSorted() === 'asc' ? '▴' : header.column.getIsSorted() === 'desc' ? '▾' : ''}</span></button></th>)}</tr>)}</thead>
          <tbody style={{ height: virtualizer.getTotalSize() }}>{virtualRows.map((virtualRow) => {
            const row = tableRows[virtualRow.index];
            if (!row) return null;
            const isSelected = row.original.symbol === selectedSymbol;
            return (
              <tr
                key={row.id}
                className={isSelected ? 'is-selected' : ''}
                style={{ transform: `translateY(${virtualRow.start}px)` }}
                tabIndex={0}
                aria-label={`${row.original.symbol}, ${row.original.contract.description}`}
                aria-selected={isSelected}
                onClick={() => onSelect(row.original.symbol)}
                onDoubleClick={() => remove(row.original.symbol)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(row.original.symbol);
                  } else if (event.key === 'Delete') {
                    event.preventDefault();
                    remove(row.original.symbol);
                  }
                }}
              >
                {row.getVisibleCells().map((cell) => <td className={`col-${cell.column.id}`} style={{ width: cell.column.getSize() }} key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>)}
              </tr>
            );
          })}</tbody>
        </table>
        {rows.length === 0 && <div className="watch-empty">{copy.empty}</div>}
      </div>
      <footer className="watchlist__foot"><span>{copy.keyboardHint}</span><time>{new Date(simulatedAt).toISOString().slice(11, 19)}</time></footer>
    </section>
  );
}
